/**
 * The `track` capability: the merchant's coded funnel reports its steps and events,
 * and the browser pixels bound on the canvas fire from here.
 *
 * Three rules hold this file together:
 * - the constructor assigns fields and nothing else (the React provider builds the
 *   client inside a `useMemo` that also runs during SSR);
 * - every public method returns at once without a `window`;
 * - everything automatic is gated on an opt-in flag that only `step()` writes, so a
 *   site that upgrades and never calls `track.step()` makes zero new request.
 */
import type { TgdBootstrap, TgdPublicApi } from '../../tgd/contract/index.js';
import type { CheckoutLineItem } from '../types.js';
import { SDK_NAME, SDK_VERSION } from '../version.js';
import type {
  PixelEngine,
  PixelEngineConfig,
  PixelSignal,
  PixelsConfig,
  StandardPixelEvent,
} from '../../pixels/index.js';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export interface StepData {
  /** Overrides `location.pathname` for display and for the dedup key. */
  path?: string;
  props?: Record<string, JsonValue>;
}

export type EventData = Record<string, JsonValue>;

export interface TrackIdentity {
  email?: string;
  phone?: string;
  customerId?: string;
}

/** One cart line, as the pixel mappers read it. Amounts stay in MINOR units. */
export interface PixelContent {
  id: string;
  quantity: number;
  item_price?: number;
}

export interface TrackSignalNote {
  signal: string;
  value: string;
  detail?: Record<string, string>;
}

/** A local view: what this page loaded and what it could not do. No server call of its own. */
export interface TrackHealth {
  enabled: boolean;
  storeId: string;
  funnelKey: string;
  sdk: { name: typeof SDK_NAME; version: typeof SDK_VERSION };
  manifestHash: string | null;
  pixels: { loaded: boolean; providers: string[] };
  signals: TrackSignalNote[];
}

// ---------------------------------------------------------------------------
// Wire shapes (contract §6, R4 and R5)
// ---------------------------------------------------------------------------

type Provider = 'facebook' | 'tiktok' | 'snapchat' | 'gtm' | 'clarity' | 'applovin';

interface ConfigPixel {
  provider: Provider;
  id: string;
  idKey: 'pixelId' | 'containerId' | 'projectId' | 'eventKey';
  events: Record<string, boolean>;
  googleAdsConversionId?: string;
  googleAdsConversionLabel?: string;
}

interface TrackConfig {
  collect?: { v: 1; url: string };
  experiments?: { v: 1 };
  version: number;
  storeId: string;
  funnelKey: string;
  manifestHash: string;
  pixels: ConfigPixel[];
  steps: Record<string, { type?: string; events: Record<string, Provider[]> }>;
  checkout: { InitiateCheckout: Provider[]; Purchase: Provider[] };
  consent: { required: boolean };
}

type TrackRecord =
  | { kind: 'step'; stepId: string; at: number; path?: string }
  | {
      kind: 'event';
      stepId: string;
      name: string;
      at: number;
      eventId: string;
      props?: Record<string, string | number | boolean>;
    }
  | {
      kind: 'signal';
      signal: 'pixel_blocked' | 'pixel_duplicate' | 'wallet_unavailable';
      value: string;
      detail?: Record<string, string>;
    };

interface PixelsModule {
  createPixelEngine(config: PixelEngineConfig): PixelEngine;
}

// ---------------------------------------------------------------------------
// Engine loading: a lazy import on npm, a second CDN file on the bundle
// ---------------------------------------------------------------------------

declare const __TGD_PIXELS_CDN__: string | undefined;
const CDN_URL = typeof __TGD_PIXELS_CDN__ === 'undefined' ? '' : __TGD_PIXELS_CDN__;

function loadScript(src: string, isReady: () => boolean): Promise<void> {
  if (isReady()) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('runtime script failed to load'));
    document.head.appendChild(script);
  });
}

async function loadPixels(): Promise<PixelsModule | null> {
  if (typeof window === 'undefined') return null;
  if (CDN_URL) {
    const w = window as unknown as { TagadaPixels?: PixelsModule };
    if (w.TagadaPixels) return w.TagadaPixels;
    await loadScript(CDN_URL, () => Boolean(w.TagadaPixels));
    return w.TagadaPixels ?? null;
  }
  return (await import('../../pixels/index.js')) as unknown as PixelsModule;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const FLAG_KEY = '__tgd_track';
const ANON_KEY = '__tgd_aid';
const SESSION_KEY = '__tgd_sid';
const QUEUE_LIMIT = 50;
const FLUSH_MS = 2000;
const DEDUP_MS = 1000;
/** Hard ceiling on the dedup map. A long-lived SPA visiting hundreds of paths must not grow it. */
const STEP_MEMORY = 64;

/** Cart lines as the pixel mappers read them. `unitAmount` stays minor; `baseTransform` converts. */
export function toContents(items?: CheckoutLineItem[]): PixelContent[] | undefined {
  if (!Array.isArray(items) || items.length === 0) return undefined;
  return items.map((item) => ({ id: item.variantId, quantity: item.quantity, item_price: item.unitAmount }));
}

/** R5 carries flat scalars only; anything nested is dropped rather than stringified. */
function scalars(data?: Record<string, JsonValue>): Record<string, string | number | boolean> | undefined {
  if (!data) return undefined;
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') out[key] = value;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Contract §3: the three SDK event names that carry a pixel meaning, and the standard event a
 * canvas binding writes for them. The back end auto-binds `Lead`, not `lead`, so an event sent
 * under its own name would be gated against a key `config.steps[...].events` never holds, and
 * would reach Meta as an unknown custom event. Any other name is a custom event.
 */
const STANDARD_EVENTS: Readonly<Record<string, StandardPixelEvent>> = {
  lead: 'Lead',
  product_viewed: 'ViewContent',
  added_to_cart: 'AddToCart',
};

function uuid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * R4 answers a flat array (one entry per configured integration); the engine reads the
 * hosted runtime's per-provider map. This is the only place the two shapes meet.
 */
function toPixelsConfig(pixels: ConfigPixel[]): PixelsConfig {
  const out: Record<string, Record<string, unknown>[]> = {};
  for (const pixel of pixels) {
    // R4 builds each entry key by key, so spreading its own answer carries nothing extra.
    const { provider, id, idKey, ...rest } = pixel;
    (out[provider] ??= []).push({ ...rest, enabled: true, [idKey]: id, events: pixel.events ?? {} });
  }
  return out as unknown as PixelsConfig;
}

// ---------------------------------------------------------------------------
// The module
// ---------------------------------------------------------------------------

export class TrackModule {
  private readonly records: TrackRecord[] = [];
  private readonly lastStep = new Map<string, number>();
  private readonly signals: TrackSignalNote[] = [];

  private currentStepId: string | null = null;
  private manifestHash: string | null = null;
  private loadedProviders: string[] = [];
  private configPromise: Promise<TrackConfig | null> | null = null;
  private corePromise: Promise<TgdPublicApi | null> | null = null;
  /** @internal Server assignment passed to the lazy runtime. */
  bootstrap?: TgdBootstrap;
  private enginePromise: Promise<PixelEngine | null> | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private listening = false;

  constructor(
    private readonly baseUrl: string,
    private readonly storeId: string,
    private readonly funnelKey: string,
  ) {}

  // -- opt-in flag ----------------------------------------------------------

  /**
   * The flag is `<storeId>:<funnelKey>`. Only the storeId half arms it; the funnel half is DATA,
   * read back by `flagFunnelKey()` so automatic events report the funnel the last `step()` named.
   * A funnel-key mismatch never disarms tracking; only another tenant's storeId reads as disabled.
   */
  private flag(): string[] {
    try {
      return (localStorage.getItem(FLAG_KEY) ?? '').split(':');
    } catch {
      return [];
    }
  }

  private enabled(): boolean {
    return this.flag()[0] === this.storeId;
  }

  private flagFunnelKey(): string {
    return this.flag()[1] || this.funnelKey;
  }

  /** Every automatic path is off under SSR and off without the flag. */
  private off(): boolean {
    return typeof window === 'undefined' || !this.enabled();
  }

  private arm(): void {
    try {
      localStorage.setItem(FLAG_KEY, `${this.storeId}:${this.funnelKey}`);
    } catch {
      /* private mode: tracking simply stays off */
    }
  }

  // -- public API -----------------------------------------------------------

  step(stepId: string, data?: StepData): void {
    if (typeof window === 'undefined') return;
    this.arm();
    const path = data?.path ?? window.location.pathname;
    const key = `${stepId}|${path}`;
    const at = Date.now();
    const previous = this.lastStep.get(key);
    if (previous !== undefined && at - previous < DEDUP_MS) return;
    this.remember(key, at);
    this.currentStepId = stepId;
    this.listen();
    this.push({ kind: 'step', stepId, at, path });
    void this.pixels().then((engine) => engine?.pageView(path, { stepId }));
    void this.core().then((t) => t?.page({ step_id: stepId })).catch(() => undefined);
  }

  event(name: string, data?: EventData): void {
    if (this.off()) return;
    const stepId = this.currentStepId;
    // An event before the first `step()` belongs to no step: it reaches neither R5 nor a pixel.
    if (stepId === null) return;
    // One id, both destinations (contract §7.5: `'<PixelEvent>_' + uuid`). The pixel event is the
    // standard name when there is one, so the id Meta receives is the id R5 recorded.
    const standard = STANDARD_EVENTS[name];
    const eventId = `${standard ?? name}_${uuid()}`;
    this.push({ kind: 'event', stepId, name, at: Date.now(), eventId, props: scalars(data) });
    void this.pixels().then((engine) =>
      standard ? engine?.track(standard, data, { stepId, eventId }) : engine?.trackCustom(name, data, { stepId }),
    );
    void this.core().then((t) => t?.track(name, { ...(scalars(data) ?? {}), step_id: stepId, pixel_event_id: eventId })).catch(() => undefined);
  }

  /** Keep the dedup map to entries still inside the window, and never past `STEP_MEMORY`. */
  private remember(key: string, at: number): void {
    for (const [seen, when] of this.lastStep) {
      if (at - when >= DEDUP_MS) this.lastStep.delete(seen);
    }
    // Map iterates in insertion order, so the first key is the oldest one still held.
    while (this.lastStep.size >= STEP_MEMORY) {
      const oldest = this.lastStep.keys().next();
      if (oldest.done === true) break;
      this.lastStep.delete(oldest.value);
    }
    this.lastStep.set(key, at);
  }

  identify(who: TrackIdentity): void {
    if (this.off()) return;
    // The engine's identity field is `externalId`; the Tagada customer id is what fills it.
    void this.pixels().then((engine) =>
      engine?.identify({ email: who.email, phone: who.phone, externalId: who.customerId }),
    );
    void this.core().then((t) => t?.identify(who.customerId ?? null, { email: who.email, phone: who.phone, customerId: who.customerId })).catch(() => undefined);
  }

  consent(state: { marketing: boolean }): void {
    if (this.off()) return;
    void this.pixels().then((engine) => engine?.consent(state));
  }

  async health(): Promise<TrackHealth> {
    if (typeof window === 'undefined') return this.snapshot();
    if (this.enabled()) await this.config();
    return this.snapshot();
  }

  // -- internal hooks, called by the checkout and payment modules -----------

  /** @internal */
  noteCheckout(input: { checkoutSessionId: string; amount?: number; currency?: string; items?: PixelContent[] }): void {
    this.notePixel('InitiateCheckout', input.checkoutSessionId, input);
  }

  /** @internal */
  notePaid(input: { orderId: string; amount?: number; currency?: string; items?: PixelContent[] }): void {
    this.notePixel('Purchase', input.orderId, input);
  }

  /** The `eventId` formula is the server's `makeMetaEventId`, so Meta and TikTok dedup. */
  private notePixel(
    event: 'InitiateCheckout' | 'Purchase',
    id: string,
    input: { amount?: number; currency?: string; items?: PixelContent[] },
  ): void {
    if (this.off()) return;
    void this.pixels().then((engine) =>
      engine?.track(
        event,
        {
          value: input.amount,
          currency: input.currency,
          contents: input.items,
          num_items: input.items?.length,
          ...(event === 'Purchase' ? { transaction_id: id } : {}),
        },
        { eventId: `${event}_${id}` },
      ),
    );
  }

  /** @internal */
  noteSignal(signal: 'wallet_unavailable', value: 'applePay' | 'googlePay' | 'express', detail: { reason: string }): void {
    if (this.off()) return;
    this.signals.push({ signal, value, detail });
    this.listen();
    this.push({ kind: 'signal', signal, value, detail });
  }

  // -- config, engine -------------------------------------------------------

  private snapshot(): TrackHealth {
    return {
      enabled: typeof window === 'undefined' ? false : this.enabled(),
      storeId: this.storeId,
      funnelKey: this.flagFunnelKey(),
      sdk: { name: SDK_NAME, version: SDK_VERSION },
      manifestHash: this.manifestHash,
      pixels: { loaded: this.loadedProviders.length > 0, providers: [...this.loadedProviders] },
      signals: [...this.signals],
    };
  }

  /**
   * R4, once per page. Plain `fetch`, no header, no credentials — never `HttpClient`,
   * which adds `Content-Type` and `x-cms-token` and so preflights against a fixed allow-list.
   */
  private config(): Promise<TrackConfig | null> {
    if (this.configPromise !== null) return this.configPromise;
    const url =
      `${this.baseUrl}/api/public/v1/sdk/config` +
      `?storeId=${encodeURIComponent(this.storeId)}&funnel=${encodeURIComponent(this.flagFunnelKey())}`;
    this.configPromise = (async (): Promise<TrackConfig | null> => {
      try {
        const response = await fetch(url);
        if (!response.ok) return null;
        const config = (await response.json()) as TrackConfig;
        this.manifestHash = config.manifestHash || null;
        return config;
      } catch {
        return null;
      }
    })();
    return this.configPromise;
  }

  /** @internal R4 is reused; loading is allowed only when it opts this store in. */
  core(): Promise<TgdPublicApi | null> {
    if (typeof window === 'undefined') return Promise.resolve(null);
    this.corePromise ??= (async () => {
      try {
        const config = await this.config();
        if (!config || (!config.collect && !config.experiments)) return null;
        const base = this.baseUrl.replace(/\/+$/, '');
        const w = window as unknown as { tgd?: TgdPublicApi };
        if (typeof w.tgd?.init !== 'function') {
          await loadScript(base + '/assets/js/tgd.js', () => typeof w.tgd?.init === 'function');
        }
        if (typeof w.tgd?.init !== 'function') return null;
        w.tgd.init({ storeId: this.storeId, apiHost: config.collect ? new URL(config.collect.url).origin : base,
          bootstrap: this.bootstrap, _source: 'headless', _manualPages: true });
        return w.tgd;
      } catch {
        return null;
      }
    })();
    return this.corePromise;
  }

  /** @internal Funnel navigation alone must not opt a site into tracking. */
  noteFunnelSession(funnelSessionId: string): void {
    if (this.off()) return;
    void this.core().then((t) => t?._link(funnelSessionId)).catch(() => undefined);
  }

  private pixels(): Promise<PixelEngine | null> {
    if (!this.enabled()) return Promise.resolve(null);
    this.enginePromise ??= this.startEngine();
    return this.enginePromise;
  }

  private async startEngine(): Promise<PixelEngine | null> {
    try {
      const config = await this.config();
      if (config === null) return null;
      const module = await loadPixels();
      if (module === null) return null;
      const engine = module.createPixelEngine({
        pixels: toPixelsConfig(config.pixels ?? []),
        steps: config.steps,
        checkout: config.checkout,
        requireConsent: config.consent?.required === true,
        onSignal: (signal: PixelSignal) => this.onPixelSignal(signal),
      });
      await engine.init();
      this.loadedProviders = [...new Set((config.pixels ?? []).map((pixel) => pixel.provider))];
      return engine;
    } catch {
      return null;
    }
  }

  private onPixelSignal(signal: PixelSignal): void {
    const detail = signal.detail ?? {};
    this.signals.push({ signal: signal.kind, value: signal.provider, detail });
    this.push({ kind: 'signal', signal: signal.kind, value: signal.provider, detail });
  }

  // -- R5 queue -------------------------------------------------------------

  private listen(): void {
    if (this.listening || typeof window === 'undefined') return;
    this.listening = true;
    const onLeave = (event: Event): void => {
      if (event.type === 'visibilitychange' && document.visibilityState !== 'hidden') return;
      this.flush();
    };
    window.addEventListener('pagehide', onLeave);
    window.addEventListener('visibilitychange', onLeave);
  }

  private push(record: TrackRecord): void {
    if (!this.enabled()) return;
    this.records.push(record);
    if (this.records.length >= QUEUE_LIMIT) {
      this.flush();
      return;
    }
    if (this.flushTimer === null) this.flushTimer = setTimeout(() => this.flush(), FLUSH_MS);
  }

  /** `localStorage` is an undeclared identifier in Node, so it is read inside the try. */
  private identifier(scope: 'local' | 'session', key: string): string {
    try {
      const store = scope === 'local' ? localStorage : sessionStorage;
      const existing = store.getItem(key);
      if (existing) return existing;
      const fresh = uuid();
      store.setItem(key, fresh);
      return fresh;
    } catch {
      return uuid();
    }
  }

  private flush(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.records.length === 0) return;
    const records = this.records.splice(0, QUEUE_LIMIT);
    const payload: Record<string, unknown> = {
      storeId: this.storeId,
      funnelKey: this.flagFunnelKey(),
      sdk: { name: SDK_NAME, version: SDK_VERSION },
      anonymousId: this.identifier('local', ANON_KEY),
      sessionId: this.identifier('session', SESSION_KEY),
      records,
    };
    if (this.manifestHash !== null) payload.manifestHash = this.manifestHash;
    const body = JSON.stringify(payload);
    const url = `${this.baseUrl}/api/public/v1/sdk/events`;
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        if (navigator.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=UTF-8' }))) return;
      }
    } catch {
      /* swallowed: tracking never surfaces an error */
    }
    try {
      void fetch(url, {
        method: 'POST',
        body,
        keepalive: true,
        headers: { 'content-type': 'text/plain;charset=UTF-8' },
      }).catch(() => undefined);
    } catch {
      /* swallowed */
    }
  }
}
